import React from 'react';
import { FiCircle, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';

const CalendarCell = ({ day, data, keywords, onClick, disabled, isCurrentDay, processing, onAccept, onReject }) => {
    const navigate = useNavigate();

    const getStatusStyles = (status) => {
        const styles = {
            "in progress": 'bg-amber-500/10 hover:bg-amber-500/20',
            "done": 'bg-green-500/10 hover:bg-green-500/20',
            "incomplete": 'bg-red-400/10 hover:bg-red-400/20',
        };
        return styles[status] || 'hover:bg-slate-700/50';
    };

    const getStatusTextColor = (status) => {
        const colors = {
            "in progress": 'text-amber-500',
            "done": 'text-green-500',
            "incomplete": 'text-red-400',
        };
        return colors[status] || 'text-slate-400';
    };

    const renderStatusIcon = (status) => {
        const icons = {
            "in progress": <FiCircle className="text-amber-500" />,
            "done": <FiCheckCircle className="text-green-500" />,
            "incomplete": <FiXCircle className="text-red-400" />,
        };
        return icons[status] || null;
    };

    const handleTitleClick = () => {
        if (!disabled && data?.articleId) {
            onClick();
        }
    };

    const simplifyType = (type) => {
        if (!type) return null;
        const normalizedType = type.trim();
        const typeMapping = {
            "Educational Essay": "Educational",
            "How-to Guide": "Guide",
            "Ultimate Guide": "Guide",
            "Opinion Article": "Opinion",
        };
        return typeMapping[normalizedType] || normalizedType.split(' ')[0];
    };

    const renderArticleTypeBadge = (type) => {
        if (!type) return null;
        const badgeClass = 'bg-gray-500 text-white';
        return (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${badgeClass}`}>
                {simplifyType(type)}
            </span>
        );
    };

    const calculateAggregatedMetrics = (keywords) => {
        if (!keywords.length) {
            return {
                totalSearchVolume: 'N/A',
                avgDifficulty: 'N/A',
                avgCpc: 'N/A',
            };
        }

        const totalSearchVolume = keywords.reduce((sum, kw) => sum + (kw.search_volume_msv || 0), 0);
        const avgDifficulty = Math.round(
            keywords.reduce((sum, kw) => sum + (kw.keyword_difficulty || 0), 0) / keywords.length
        );
        const avgCpc = (keywords.reduce((sum, kw) => sum + parseFloat(kw.cpc || 0), 0) / keywords.length).toFixed(2);

        return { totalSearchVolume, avgDifficulty, avgCpc };
    };

    const { totalSearchVolume, avgDifficulty, avgCpc } = calculateAggregatedMetrics(keywords);

    const handleAcceptClick = (e) => {
        e.stopPropagation();
        if (onAccept) {
            onAccept(data.articleId);
        }
    };

    const handleRejectClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onReject) {
            onReject(data.articleId);
        }
    };

    return (
        <div
            className={`min-h-[80px] sm:min-h-[100px] md:min-h-[120px] p-2 sm:p-3 bg-slate-800 transition-colors ${
                getStatusStyles(data?.status)
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
                isCurrentDay ? 'border-4 border-blue-500' : 'border border-transparent'
            } relative`}
            onClick={handleTitleClick}
        >
            <div className="flex flex-col h-full">
                {/* Day and Badge */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-slate-400 text-xs sm:text-sm">{day}</span>
                    {data?.type && renderArticleTypeBadge(data.type)}
                </div>

                {/* Title and Status */}
                {data && data.title && (
                    <div className="flex flex-col gap-0.5 sm:gap-1">
                        <span
                            className={`text-white text-xs sm:text-sm font-medium line-clamp-2 hover:underline ${disabled ? 'cursor-not-allowed' : ''}`}
                        >
                            {data.title}
                        </span>
                        {data.status && (
                            <div className="flex items-center gap-1">
                                {renderStatusIcon(data.status)}
                                <span
                                    className={`text-[10px] sm:text-xs capitalize ${getStatusTextColor(data.status)}`}
                                >
                                    {data.status}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Keywords Section */}
                {data?.status && keywords?.length > 0 && (
                    <div className="mt-2 flex gap-1 items-center flex-wrap">
                        <div
                            className="flex items-center gap-1 bg-slate-700/50 rounded px-1.5 py-0.5 text-xs text-gray-300"
                            data-tooltip-id={`tooltip-searchvol-${data?.articleId}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 text-purple-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4h13M8 20h13M3 12h13" />
                            </svg>
                            <span>{totalSearchVolume}</span>
                        </div>
                        <Tooltip id={`tooltip-searchvol-${data?.articleId}`} className="bg-gray-700" content="Search Volume" />

                        <div
                            className="flex items-center gap-1 bg-slate-700/50 rounded px-1.5 py-0.5 text-xs text-gray-300"
                            data-tooltip-id={`tooltip-difficulty-${data?.articleId}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 text-blue-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h11M9 21l3-11L9 3" />
                            </svg>
                            <span>{avgDifficulty}</span>
                        </div>
                        <Tooltip id={`tooltip-difficulty-${data?.articleId}`} className="bg-gray-700" content="Avg Difficulty" />

                        <div
                            className="flex items-center gap-1 bg-slate-700/50 rounded px-1.5 py-0.5 text-xs text-gray-300"
                            data-tooltip-id={`tooltip-cpc-${data?.articleId}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 text-orange-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8c-2.21 0-4 1.79-4 4m0 0c0 1.11.89 2 2 2m2-2c1.11 0 2-.89 2-2m0 0c0-1.11-.89-2-2-2m2 2c1.11 0 2 .89 2 2m0 0c0 1.11-.89 2-2 2"
                                />
                            </svg>
                            <span>{avgCpc}</span>
                        </div>
                        <Tooltip id={`tooltip-cpc-${data?.articleId}`} className="bg-gray-700" content="Avg CPC" />
                    </div>
                )}
            </div>

            {data?.status === 'incomplete' && (
                <div className="absolute top-1 right-1 flex gap-1 items-center">
                    {processing ? (
                        <div className="flex items-center gap-2">
                            <FaSpinner className="animate-spin text-gray-400" size={16} />
                        </div>
                    ) : (
                        <div className="flex gap-1">
                            <button
                                onClick={handleAcceptClick}
                                className="p-1 rounded hover:bg-green-500/20 transition-colors duration-200"
                                aria-label="Accept"
                            >
                                <FaCheck className="text-green-400 hover:text-green-300" size={16} />
                            </button>
                            <button
                                onClick={handleRejectClick}
                                className="p-1 rounded hover:bg-red-500/20 transition-colors duration-200"
                                aria-label="Reject"
                            >
                                <FaTimes className="text-red-400 hover:text-red-300" size={16} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CalendarCell;
